import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';
import { formatInET } from '../utils/helper';

export interface ChatMessage {
    id: string;
    type: 'user' | 'bot';
    text: string;
    timestamp: string;
}

export interface ChatbotQueryRequest {
    Session_id: string;
    BaseUrl: string;
    Query: string;
    Currentdatetime: string;
    PortalId: number;
}

/** A single structured record row (contract / support request / payment) in a chatbot payload. */
export type ChatbotRecord = Record<string, string | number | boolean | null | object>;

/**
 * Inner payload of the nested (legacy) envelope. Also describes the object form of `data` in the
 * flat envelope's sibling — carries the human `message`, the typed record array and a `follow_up`.
 */
export interface ChatbotPayload {
    statusCode?: number;
    status_code?: number;
    status?: number;
    response?: string;
    message?: string;
    data?: ChatbotRecord[] | string;
    follow_up?: string | null;
}

export interface ChatbotQueryResponse {
    statusCode?: number;
    status_code?: number;
    // Flat envelope -> records sit directly on `data`; nested envelope -> `data` is a ChatbotPayload;
    // text responses -> `data` is the conversational string.
    data?: ChatbotPayload | ChatbotRecord[] | string;
    message?: string;
    follow_up?: string | null;
}

/** Error enriched with the HTTP/status context callers rely on for retry & display decisions. */
export type EnrichedError = Error & {
    status?: number;
    statusCode?: number;
    response?: ApiErrorResponse;
    botMessage?: string;
};

/**
 * The body the backend / axios interceptor hands us on failure. The useful text can live as a raw
 * string or under `data` / `message` / `error`, and the body itself may be wrapped in `response.data`.
 * Modeling it explicitly lets the error handlers read every location type-safely — no `any`/`unknown`.
 */
export interface ApiErrorBody {
    statusCode?: number;
    status_code?: number;
    status?: number;
    data?: ApiErrorBody | string;
    message?: string;
    error?: string;
}

export interface ApiErrorResponse {
    status?: number;
    data?: ApiErrorBody | string;
}

export interface ApiError {
    response?: ApiErrorResponse;
    data?: ApiErrorBody | string;
    status?: number;
    statusCode?: number;
    status_code?: number;
    message?: string;
    code?: string;
}

/** Narrow an unknown thrown value to the API error shape so its fields can be read type-safely. */
const asApiError = (error: unknown): ApiError =>
    typeof error === 'object' && error !== null ? (error as ApiError) : {};

class ChatbotService {
    private capitalizeSentenceStarts(text: string): string {
        if (!text) {
            return text;
        }

        // Uppercase the first letter of each sentence with a single linear left-to-right scan
        // instead of a regex. The replaced pattern /(^|[.!?]\s+|\n\s*)(["'([{]*)([a-z])/g was flagged
        // for potential super-linear backtracking on adversarial input; an explicit O(n) char walk
        // removes any regex from the hot path. Behavior is identical to the original (verified by an
        // exhaustive + fuzz equivalence check): a lowercase letter is capitalized when it sits at a
        // sentence start, where a sentence start is
        //   - the very beginning of the string, or
        //   - immediately after sentence punctuation (. ! ?) followed by one or more whitespace, or
        //   - immediately after a newline optionally followed by whitespace,
        // with any run of opening wrapper characters ("'([{) allowed between the boundary's
        // whitespace and the letter, but NOT separated from the letter by further whitespace.
        const isWrapper = (ch: string): boolean => ch === '"' || ch === "'" || ch === '(' || ch === '[' || ch === '{';
        const isLower = (ch: string): boolean => ch >= 'a' && ch <= 'z';
        const isWhitespace = (ch: string): boolean => /\s/.test(ch);

        const chars = Array.from(text);
        const len = chars.length;

        // Capitalize the letter at `start`, skipping over any leading wrapper chars first. A letter
        // is only capitalized when wrappers (if any) sit flush against it — matching the regex's
        // contiguous (wrappers)(letter) groups. Returns nothing; mutates `chars` in place.
        const capitalizeAt = (start: number): void => {
            let j = start;
            while (j < len && isWrapper(chars[j])) {
                j += 1;
            }
            if (j < len && isLower(chars[j])) {
                chars[j] = chars[j].toUpperCase();
            }
        };

        // Start of string.
        capitalizeAt(0);

        for (let i = 0; i < len; i += 1) {
            const ch = chars[i];

            if (ch === '\n') {
                // Newline boundary: skip a run of following whitespace, then capitalize.
                let j = i + 1;
                while (j < len && isWhitespace(chars[j])) {
                    j += 1;
                }
                capitalizeAt(j);
            } else if (ch === '.' || ch === '!' || ch === '?') {
                // Punctuation boundary: require at least one whitespace char before the letter.
                let j = i + 1;
                let whitespaceCount = 0;
                while (j < len && isWhitespace(chars[j])) {
                    j += 1;
                    whitespaceCount += 1;
                }
                if (whitespaceCount > 0) {
                    capitalizeAt(j);
                }
            }
        }

        const sentenceCased = chars.join('');

        return sentenceCased.replace(/\bi['’]m\b/gi, "I'm");
    }

    /**
     * Generate a unique session ID
     */
    generateSessionId(): string {
        // The session id is only a conversation-correlation key (auth is enforced separately via the
        // Bearer token), but we still use the platform crypto RNG to avoid Math.random() collisions
        // and satisfy weak-PRNG scanners. Falls back to crypto.getRandomValues, then to a
        // timestamp-based id, for environments without these APIs.
        const cryptoObj = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;

        if (cryptoObj?.randomUUID) {
            return cryptoObj.randomUUID();
        }

        if (cryptoObj?.getRandomValues) {
            const bytes = new Uint8Array(16);
            cryptoObj.getRandomValues(bytes);
            const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
            return `${Date.now()}-${hex}`;
        }

        // Last-resort fallback (non-crypto environments only): keep a unique-enough id.
        return `${Date.now()}-${Date.now().toString(36)}`;
    }

    /**
     * Format current datetime as MM-DD-YYYY
     */
    formatCurrentDateTime(): string {
        return formatInET(new Date());
    }

    /**
     * Send a query to the chatbot
     * @param query - User's query
     * @param sessionId - Session ID for maintaining conversation
     * @param authToken - Authorization token (Bearer token)
     * @param baseUrl - Base URL for the API
     * @param portalId - Portal ID (1 for Avant, 2 for AuxCap)
     * @returns Promise with bot response
     */
    async sendQuery(
        query: string,
        sessionId: string,
        authToken?: string,
        baseUrl: string = window.location.origin,
        portalId: number = 2,
    ): Promise<ChatbotQueryResponse> {
        try {
            // Strip trailing slashes with a linear index scan instead of /\/+$/ — no regex means no
            // backtracking concern on adversarial input, and the result is identical.
            const rawBaseUrl = process.env.REACT_APP_API_URL || baseUrl || window.location.origin;
            let baseUrlEnd = rawBaseUrl.length;
            while (baseUrlEnd > 0 && rawBaseUrl.charCodeAt(baseUrlEnd - 1) === 47 /* '/' */) {
                baseUrlEnd -= 1;
            }
            const resolvedBaseUrl = rawBaseUrl.slice(0, baseUrlEnd);
            const payload: ChatbotQueryRequest = {
                Session_id: sessionId,
                BaseUrl: resolvedBaseUrl,
                Query: query,
                Currentdatetime: this.formatCurrentDateTime(),
                PortalId: portalId,
            };

            const customHeaders: Record<string, string> = authToken ? { Authorization: `Bearer ${authToken}` } : {};

            // axios resolves to { data: <body>, ... }; the chatbot body itself is the envelope.
            const response: { data?: ChatbotQueryResponse } = await api_service.post({
                endPoint: api_urls.ChatbotLoanBot,
                payLoad: payload,
                customHeaders,
                showLoader: false,
            });

            return response?.data ?? (response as ChatbotQueryResponse);
        } catch (error) {
            console.error('Chatbot service error:', error);
            // Preserve 401 so caller can handle session expiry / refresh token
            if (asApiError(error).response?.status === 401) {
                throw error;
            }
            throw this.handleError(error);
        }
    }

    /**
     * Extract bot response message from API response
     */
    /**
     * Serialize a structured (array) payload back to a consistent FLAT JSON string so
     * MessageBubble's parsers (which read `parsedPayload.data`) get clean, parseable JSON.
     * Handles both envelope shapes; returns null when there is no array payload to serialize.
     */
    private serializeStructuredPayload(
        response: ChatbotQueryResponse,
        nestedPayload: ChatbotPayload | undefined,
    ): string | null {
        // Flat (current): records sit directly on response.data.
        const flatPayload: ChatbotPayload | null = Array.isArray(response?.data)
            ? {
                  statusCode: response?.statusCode ?? response?.status_code,
                  message: typeof response?.message === 'string' ? response.message : undefined,
                  data: response.data,
                  follow_up: response?.follow_up,
              }
            : // Nested (legacy): records sit on response.data.data.
              nestedPayload && Array.isArray(nestedPayload.data)
              ? nestedPayload
              : null;

        if (!flatPayload) {
            return null;
        }
        try {
            return JSON.stringify(flatPayload);
        } catch {
            return null;
        }
    }

    extractBotMessage(response: ChatbotQueryResponse): string {
        // Structured JSON payloads (contracts / support requests / payment history) arrive in two
        // envelope shapes:
        //   - Flat (current):  { status_code, message, data: [...], follow_up }
        //   - Nested (legacy): { status_code, data: { statusCode, message, data: [...], follow_up } }
        // The meaningful array of records is what the renderer needs — serialize it so the array
        // isn't silently dropped and only the header `message` shown.
        const nestedPayload: ChatbotPayload | undefined =
            response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
                ? response.data
                : undefined;

        const structured = this.serializeStructuredPayload(response, nestedPayload);
        if (structured) {
            return structured;
        }

        // Envelope whose `data` is a delimited/multi-line STRING carrying a SEPARATE header `message`
        // (e.g. { message: "Here are your payment details:", data: "Contract ID: ... | ..." }). The
        // header can live at the top level (data string sits on response) or one level down (nested
        // payload). Preserve BOTH by serializing, so the renderer can show the header instead of
        // echoing the first data row as the title. Only when the header is meaningful and distinct
        // from the body — otherwise we fall through to plain-text extraction below (unchanged).
        const headerSource: ChatbotPayload | undefined =
            typeof response?.data === 'string' && typeof response?.message === 'string'
                ? { message: response.message, data: response.data, follow_up: response.follow_up }
                : typeof nestedPayload?.data === 'string' && typeof nestedPayload?.message === 'string'
                  ? nestedPayload
                  : undefined;
        if (
            headerSource &&
            typeof headerSource.data === 'string' &&
            typeof headerSource.message === 'string' &&
            headerSource.message.trim() &&
            headerSource.message.trim() !== headerSource.data.trim()
        ) {
            try {
                return JSON.stringify({
                    statusCode: headerSource.statusCode ?? headerSource.status_code,
                    message: headerSource.message,
                    data: headerSource.data,
                    follow_up: headerSource.follow_up,
                });
            } catch {
                // Fall through to the text-based extraction below.
            }
        }

        // No structured array — fall back to the first conversational text we can find, in order:
        //   response.data (string) -> data.data -> data.response -> data.message -> response.message
        const textCandidates: Array<string | undefined> = [
            typeof response?.data === 'string' ? response.data : undefined,
            typeof nestedPayload?.data === 'string' ? nestedPayload.data : undefined,
            nestedPayload?.response,
            nestedPayload?.message,
            response?.message,
        ];
        const text = textCandidates.find((value): value is string => typeof value === 'string');

        return text ? this.capitalizeSentenceStarts(text) : 'Unable to process your request. Please try again.';
    }

    /**
     * Extract follow-up prompt from chatbot response
     */
    extractFollowUp(response: ChatbotQueryResponse): string | null {
        const nestedPayload: ChatbotPayload | undefined =
            response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
                ? response.data
                : undefined;

        const followUp = response?.follow_up ?? nestedPayload?.follow_up;
        return typeof followUp === 'string' && followUp.trim() ? followUp.trim() : null;
    }

    /**
     * Some chatbot paths return HTTP 200 with an expiry text instead of 401.
     * Detect those responses so caller can refresh token silently and retry.
     */
    isTokenExpiredMessage(message: string): boolean {
        if (!message) {
            return false;
        }

        const normalizedMessage = message.toLowerCase();
        const knownAuthPhrases = [
            /session\s+token\s+(has\s+)?expired/,
            /expired\s+session\s+token/,
            /session\s+(has\s+)?expired/,
            /session\s+issue/,
            /invalid\s+session/,
            /session\s+invalid/,
            /session\s+timed\s*out/,
            /session\s+timeout/,
            /token\s+(has\s+)?expired/,
            /expired\s+token/,
            /invalid\s+token/,
            /token\s+invalid/,
            /access\s+token\s+expired/,
            /jwt\s+expired/,
            /unauthori[sz]ed/,
            /forbidden/,
            /authentication\s+failed/,
            /not\s+authenticat/,
            /not\s+authori[sz]ed/,
        ];

        if (knownAuthPhrases.some(pattern => pattern.test(normalizedMessage))) {
            return true;
        }

        const hasSessionOrTokenWord = /(session|token|authorization|authentication|auth|jwt)/.test(normalizedMessage);
        const hasFailureWord = /(expired|invalid|issue|timeout|timed out|unauthori[sz]ed|forbidden)/.test(
            normalizedMessage,
        );
        const hasReloginPrompt =
            /(log\s*in|login|sign\s*in)\s+again/.test(normalizedMessage) ||
            normalizedMessage.includes('please log in') ||
            normalizedMessage.includes('please login') ||
            normalizedMessage.includes('authenticate again') ||
            normalizedMessage.includes('reauthenticate') ||
            normalizedMessage.includes('try again');

        return hasSessionOrTokenWord && (hasFailureWord || hasReloginPrompt);
    }

    /**
     * Determine if response indicates auth/session failure even when HTTP is 200.
     */
    isAuthFailureResponse(response: ChatbotQueryResponse, extractedMessage?: string): boolean {
        const nestedData: ChatbotPayload | undefined =
            response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
                ? response.data
                : undefined;

        const statusCode =
            response?.statusCode ??
            response?.status_code ??
            nestedData?.statusCode ??
            nestedData?.status_code ??
            nestedData?.status;

        if ([401, 403, 419, 440, 498, 499].includes(Number(statusCode))) {
            return true;
        }

        const messageToCheck = extractedMessage || this.extractBotMessage(response);
        return this.isTokenExpiredMessage(messageToCheck);
    }

    /**
     * Pull a human-readable message out of whatever error/payload shape we were handed.
     * The axios interceptor rejects with the RAW response body for non-2xx (e.g.
     * { status_code: 404, data: "The contract ID ... was not found ..." }), so the value
     * often isn't an Error and has no `.response`. Check every place the text can live.
     */
    /**
     * Backend errors sometimes wrap the human-readable text in noise, e.g.
     *   "HTTP error: {\"statusCode\":404,\"message\":\"Contract 693746 not found for this user\"}"
     * Pull the cleanest message out: the inner JSON's `message`/`error` if present, otherwise the
     * text after a leading "HTTP error:"/"Error:" prefix, otherwise the string as-is.
     */
    private cleanErrorMessage(raw: string): string {
        // Bound input length defensively: this runs on server-supplied error text and recurses,
        // so cap the size before any scanning to keep it cheap regardless of upstream content.
        const trimmed = raw.trim().slice(0, 10000);
        if (!trimmed) {
            return '';
        }

        // Extract the embedded JSON object (first "{" to last "}") via index lookups instead of a
        // regex, so there is no backtracking on adversarial input. Equivalent to /\{[\s\S]*\}/.
        const firstBrace = trimmed.indexOf('{');
        const lastBrace = trimmed.lastIndexOf('}');
        const embeddedJson =
            firstBrace !== -1 && lastBrace > firstBrace ? trimmed.slice(firstBrace, lastBrace + 1) : '';
        if (embeddedJson) {
            try {
                const parsed = JSON.parse(embeddedJson);
                const inner =
                    (typeof parsed?.message === 'string' && parsed.message) ||
                    (typeof parsed?.error === 'string' && parsed.error) ||
                    '';
                if (inner.trim()) {
                    return this.cleanErrorMessage(inner);
                }
            } catch {
                // Not valid JSON — fall through to prefix stripping.
            }
        }

        return trimmed.replace(/^(?:HTTP\s+)?error\s*:\s*/i, '').trim();
    }

    /** Pull the first non-empty text out of an error body that may be a string or a nested object. */
    private textFromErrorBody(body: ApiErrorBody | string | undefined): string {
        if (typeof body === 'string') {
            return body.trim();
        }
        if (!body) {
            return '';
        }
        const nested = typeof body.data === 'string' ? body.data : '';
        // Surface `data`, then `message`, then the `error` field — whichever carries text first.
        return (nested || body.message || body.error || '').trim();
    }

    private extractErrorBotMessage(error: unknown): string | null {
        const err = asApiError(error);
        const candidates: Array<ApiErrorBody | string | undefined> = [
            err.response?.data,
            err.data,
            // The raw rejected body can also be the error object itself.
            typeof err.message === 'string' ? err.message : undefined,
        ];

        for (const candidate of candidates) {
            const text = this.textFromErrorBody(candidate);
            if (text) {
                return this.cleanErrorMessage(text);
            }
        }
        return null;
    }

    /**
     * Resolve an HTTP-ish status code from any error/payload shape, including the raw
     * rejected body (`status_code` / nested `data.statusCode`).
     */
    private extractErrorStatus(error: unknown): number {
        const err = asApiError(error);
        const body = typeof err.response?.data === 'object' ? err.response?.data : undefined;
        const dataBody = typeof err.data === 'object' ? err.data : undefined;
        const raw =
            err.response?.status ??
            err.status ??
            err.statusCode ??
            err.status_code ??
            body?.statusCode ??
            body?.status_code ??
            dataBody?.statusCode ??
            dataBody?.status_code;
        return Number(raw);
    }

    /**
     * Handle API errors
     */
    private handleError(error: unknown): EnrichedError {
        const err = asApiError(error);
        const status = this.extractErrorStatus(error);

        if (status === 401) {
            const unauthorizedError = new Error('Unauthorized access. Please login again.') as EnrichedError;
            unauthorizedError.status = 401;
            unauthorizedError.response = err.response;
            return unauthorizedError;
        }
        if (err.code === 'ECONNABORTED') {
            return new Error('Request timeout. Please try again.') as EnrichedError;
        }

        const botMessage = this.extractErrorBotMessage(error);

        if (error instanceof Error) {
            const enrichedError = error as EnrichedError;
            if (Number.isFinite(status)) {
                enrichedError.status = status;
                enrichedError.statusCode = status;
            }
            // `response` (if any) already lives on the Error instance — nothing to copy.
            if (botMessage) {
                enrichedError.botMessage = botMessage;
            }
            return enrichedError;
        }

        const fallbackError = new Error(
            botMessage || err.message || 'An error occurred while sending your message.',
        ) as EnrichedError;
        if (Number.isFinite(status)) {
            fallbackError.status = status;
            fallbackError.statusCode = status;
        }
        if (err.response) {
            fallbackError.response = err.response;
        }
        if (botMessage) {
            fallbackError.botMessage = botMessage;
        }
        return fallbackError;
    }
}

export const chatbotService = new ChatbotService();
